// MEMBRESÍAS F1 (20-oct-2026, Carlos «comienza con la primer etapa f1»). Página a TODO LO ANCHO (regla del manager):
// planes del negocio (crear/editar/pausar/archivar) + miembros (vigentes / por vencer / vencidas) con vender, renovar y
// cancelar. Solo aparece en el menú con loyalty_flags.memberships; por URL sin el flag enseña un aviso.
// Backend: routes/loyalty.js (/membership-plans, /memberships). Diseño: docs/lealify-membresias-plan-20261020.md
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, Loader2, Plus, Pencil, Archive, Pause, Play, RefreshCw, X, Users, Bell } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Layout from '../../../components/layout/Layout';
import PestanasModulo, { usePestanaActiva } from '../../../components/common/PestanasModulo';
import FilasMensajes from '../../whatsapp/components/FilasMensajes';
import { useAuth } from '../../../context/AuthContext';
import { membershipsApi, describeBeneficio, fechaCorta, fmtVence, fmtVigencia, STATUS_LABEL,
  type ContactMembership, type MembershipCounts, type MembershipFilter, type MembershipPlan } from '../../../api/membershipsApi';
import { rewardsApi } from '../../../api/rewardsApi';
import MembershipPlanForm from '../components/MembershipPlanForm';
import SellMembershipModal from '../components/SellMembershipModal';

const money = (n: number) => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const tel4 = (p?: string | null) => (p ? `${String(p).slice(0, 3)}…${String(p).slice(-2)}` : '');

// F3 reorden (24-oct-2026): dos pestañas. «Avisos» enseña los dos mensajes automáticos de membresías (por vencer /
// vencida) con su estado; editar abre el editor de siempre en Mensajes automáticos.
const MEMB_TABS = [
  { id: 'planes', label: 'Planes y miembros', icon: Users },
  { id: 'avisos', label: 'Avisos', icon: Bell },
];

const MembershipsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const tab = usePestanaActiva(['planes', 'avisos']);
  const puedeEditar = !currentUser?.baseRole || currentUser.baseRole === 'owner' || currentUser.baseRole === 'manager';
  const loyaltyMode = (currentUser?.loyaltyMode || 'visits') as 'visits' | 'cashback';
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [rewards, setRewards] = useState<{ id: string; title: string; isActive?: boolean }[]>([]);
  const [filter, setFilter] = useState<MembershipFilter>('active');
  const [rows, setRows] = useState<ContactMembership[] | null>(null);
  const [counts, setCounts] = useState<MembershipCounts>({ active: 0, expiring: 0, expired: 0 });
  const [planModal, setPlanModal] = useState<{ open: boolean; plan: MembershipPlan | null }>({ open: false, plan: null });
  const [sell, setSell] = useState<{ open: boolean; contact: { id: string; name: string } | null; current: ContactMembership | null }>({ open: false, contact: null, current: null });
  const [busyId, setBusyId] = useState<number | null>(null);

  const cargarPlanes = useCallback(async () => {
    try { const r = await membershipsApi.plans(); setEnabled(r.enabled); setPlans(r.plans); }
    catch { setEnabled(false); }
  }, []);
  const cargarMiembros = useCallback(async (f: MembershipFilter) => {
    try { const r = await membershipsApi.list(f); setRows(r.memberships); setCounts(r.counts); }
    catch { setRows([]); }
  }, []);
  useEffect(() => { cargarPlanes(); rewardsApi.list().then((l) => setRewards(l.map((r) => ({ id: r.id, title: r.title, isActive: r.isActive })))).catch(() => {}); }, [cargarPlanes]);
  useEffect(() => { setRows(null); cargarMiembros(filter); }, [filter, cargarMiembros]);

  const activos = useMemo(() => plans.filter((p) => p.status !== 'archived'), [plans]);

  const pausar = async (p: MembershipPlan) => {
    setBusyId(p.id);
    try { await membershipsApi.updatePlan(p.id, { status: p.status === 'active' ? 'inactive' : 'active' }); cargarPlanes(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo cambiar'); }
    finally { setBusyId(null); }
  };
  const archivar = async (p: MembershipPlan) => {
    if (!window.confirm(`¿Archivar «${p.name}»? Ya no se venderá; quien ya lo tiene lo conserva hasta que venza.`)) return;
    setBusyId(p.id);
    try { await membershipsApi.archivePlan(p.id); cargarPlanes(); toast.success('Plan archivado'); }
    catch (e: any) { toast.error(e?.message || 'No se pudo archivar'); }
    finally { setBusyId(null); }
  };
  const cancelar = async (m: ContactMembership) => {
    const motivo = window.prompt(`¿Cancelar la membresía ${m.plan_name} de ${m.first_name || 'este cliente'}? Motivo (opcional):`);
    if (motivo === null) return;
    setBusyId(m.id);
    try { await membershipsApi.cancel(m.contact_id, motivo); toast.success('Membresía cancelada'); cargarMiembros(filter); cargarPlanes(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo cancelar'); }
    finally { setBusyId(null); }
  };
  const abrirVenta = (m?: ContactMembership) => setSell({
    open: true,
    contact: m ? { id: String(m.contact_id), name: [m.first_name, m.last_name].filter(Boolean).join(' ') || 'Cliente' } : null,
    current: m || null,
  });

  const chips: { k: MembershipFilter; t: string; n?: number }[] = [
    { k: 'active', t: 'Vigentes', n: counts.active }, { k: 'expiring', t: 'Por vencer', n: counts.expiring }, { k: 'expired', t: 'Vencidas', n: counts.expired }, { k: 'all', t: 'Todas' },
  ];

  return (
    <Layout>
      <div className="space-y-6 px-2 sm:px-0" data-testid="membresias-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
              <BadgeCheck className="h-6 w-6 mr-2 text-emerald-600" /> Membresías
            </h1>
            <p className="text-sm text-content-3 mt-1">Vende un plan por meses o por productos; el kiosco y la caja lo reconocen y te avisa antes de que venza.</p>
          </div>
          {enabled && puedeEditar && tab === 'planes' && (
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setPlanModal({ open: true, plan: null })} data-testid="btn-nuevo-plan"
                className="inline-flex items-center gap-1 rounded-md border border-line px-3 py-2 text-sm font-medium text-content-2 hover:bg-surface-2">
                <Plus className="h-4 w-4" /> Nuevo plan
              </button>
              <button type="button" onClick={() => abrirVenta()} disabled={!activos.some((p) => p.status === 'active')} data-testid="btn-vender"
                className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                <BadgeCheck className="h-4 w-4" /> Vender membresía
              </button>
            </div>
          )}
        </div>

        {enabled && <PestanasModulo tabs={MEMB_TABS} activa={tab} testid="membresias-tabs" />}

        {enabled === null ? (
          <p className="flex items-center gap-1 text-sm text-content-3"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</p>
        ) : !enabled ? (
          <div className="bg-surface p-5 rounded-lg border border-line shadow-sm text-sm text-content-2" data-testid="membresias-off">
            Las membresías todavía no están activas en tu negocio. Escríbenos por WhatsApp y las encendemos.
          </div>
        ) : tab === 'avisos' ? (
          <div className="space-y-3" data-testid="membresias-avisos">
            <p className="text-sm text-content-2">Lealify avisa solo al cliente cuando su membresía está por vencer y cuando ya venció, por correo o por WhatsApp según el canal que elijas en cada aviso. Toca uno para encenderlo, elegir el canal o cambiar el texto.</p>
            <FilasMensajes claves={['membership_expiring', 'membership_expired']} testid="membresias-avisos-lista" />
          </div>
        ) : (
          <>
            {/* ── Planes ── */}
            <section className="bg-surface p-5 rounded-lg border border-line shadow-sm">
              <h2 className="text-lg font-medium text-content mb-1">Planes{activos.length ? ` (${activos.length})` : ''}</h2>
              {activos.length === 0 ? (
                <p className="text-sm text-content-3" data-testid="planes-vacio">Aún no tienes planes. Crea el primero: por ejemplo «Club del Café: 1 mes, un café de cortesía por visita».</p>
              ) : (
                <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="planes-lista">
                  {activos.map((p) => (
                    <li key={p.id} className={`rounded-lg border p-4 ${p.status === 'active' ? 'border-line' : 'border-dashed border-line opacity-70'}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-content truncate">{p.name}</p>
                          <p className="text-xs text-content-3">{fmtVigencia(p)}{p.price != null ? ` · ${money(p.price)}` : ''}</p>
                        </div>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${p.status === 'active' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300' : 'bg-surface-2 text-content-3'}`}>
                          {p.status === 'active' ? 'A la venta' : 'Pausado'}
                        </span>
                      </div>
                      {p.description && <p className="mt-1 text-xs text-content-2 line-clamp-2">{p.description}</p>}
                      <ul className="mt-2 flex flex-wrap gap-1">
                        {!p.accrues_points && <li className="rounded-full border border-line px-2 py-0.5 text-[11px] text-content-3">No acumula puntos</li>}
                        {p.benefits.map((r) => <li key={r.type} className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300">{describeBeneficio(r, rewards, loyaltyMode)}</li>)}
                        {p.benefits.length === 0 && p.accrues_points && <li className="text-[11px] text-content-3">Sin beneficios configurados</li>}
                      </ul>
                      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
                        <span className="text-content-3">{p.members_active || 0} {p.members_active === 1 ? 'miembro vigente' : 'miembros vigentes'}</span>
                        {puedeEditar && (
                          <span className="flex gap-2">
                            <button type="button" onClick={() => setPlanModal({ open: true, plan: p })} className="inline-flex items-center gap-1 text-content-2 hover:underline"><Pencil className="h-3 w-3" /> Editar</button>
                            <button type="button" onClick={() => pausar(p)} disabled={busyId === p.id} className="inline-flex items-center gap-1 text-content-2 hover:underline disabled:opacity-50">
                              {p.status === 'active' ? <><Pause className="h-3 w-3" /> Pausar</> : <><Play className="h-3 w-3" /> Vender de nuevo</>}
                            </button>
                            <button type="button" onClick={() => archivar(p)} disabled={busyId === p.id} className="inline-flex items-center gap-1 text-content-3 hover:text-red-600 hover:underline disabled:opacity-50"><Archive className="h-3 w-3" /> Archivar</button>
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* ── Miembros ── */}
            <section className="bg-surface p-5 rounded-lg border border-line shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h2 className="text-lg font-medium text-content">Miembros</h2>
                <div className="flex flex-wrap gap-1.5" data-testid="miembros-filtros">
                  {chips.map((c) => (
                    <button key={c.k} type="button" onClick={() => setFilter(c.k)} aria-pressed={filter === c.k}
                      className={`rounded-full border px-3 py-1 text-xs ${filter === c.k ? 'border-emerald-500 bg-emerald-500/10 font-semibold text-emerald-700 dark:text-emerald-300' : 'border-line text-content-2 hover:bg-surface-2'}`}>
                      {c.t}{c.n != null ? ` (${c.n})` : ''}
                    </button>
                  ))}
                </div>
              </div>
              {rows === null ? (
                <p className="flex items-center gap-1 text-xs text-content-3"><Loader2 className="h-3 w-3 animate-spin" /> Cargando…</p>
              ) : rows.length === 0 ? (
                <p className="text-sm text-content-3" data-testid="miembros-vacio">
                  {filter === 'active' ? 'Nadie tiene membresía vigente todavía.' : filter === 'expiring' ? 'Nada por vencer esta semana.' : filter === 'expired' ? 'Sin vencidas en los últimos 90 días.' : 'Sin membresías.'}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm" data-testid="miembros-tabla">
                    <thead>
                      <tr className="text-left text-xs text-content-3">
                        <th className="py-2 pr-3 font-medium">Cliente</th>
                        <th className="py-2 pr-3 font-medium">Plan</th>
                        <th className="py-2 pr-3 font-medium">Desde</th>
                        <th className="py-2 pr-3 font-medium">Vence</th>
                        <th className="py-2 pr-3 font-medium">Incluidos</th>
                        <th className="py-2 pr-3 font-medium">Estado</th>
                        {puedeEditar && <th className="py-2 font-medium"></th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line">
                      {rows.map((m) => {
                        const porVencer = m.status === 'active' && ((m.days_left != null && m.days_left <= 7) || (m.quota_total != null && m.quota_left != null && m.quota_left <= Math.max(1, Math.ceil(m.quota_total * 0.2))));
                        return (
                          <tr key={m.id} className="align-top">
                            <td className="py-2 pr-3">
                              <Link to={`/dashboard/clients/${m.contact_id}/history`} className="font-medium text-content hover:underline">{[m.first_name, m.last_name].filter(Boolean).join(' ') || 'Cliente'}</Link>
                              <span className="ml-2 text-xs text-content-3">{tel4(m.phone)}</span>
                            </td>
                            <td className="py-2 pr-3 text-content-2">{m.plan_name}{m.renewals ? <span className="ml-1 text-xs text-content-3">· renovada ×{m.renewals}</span> : null}</td>
                            <td className="py-2 pr-3 text-content-2 whitespace-nowrap">{fechaCorta(m.starts_on)}</td>
                            <td className={`py-2 pr-3 whitespace-nowrap ${porVencer ? 'text-amber-700 dark:text-amber-300 font-medium' : 'text-content-2'}`}>{fmtVence(m)}</td>
                            <td className="py-2 pr-3 text-content-2 whitespace-nowrap">{m.quota_total != null ? `${m.quota_left} de ${m.quota_total}` : '—'}</td>
                            <td className="py-2 pr-3">
                              <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${m.status === 'active' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300' : 'bg-surface-2 text-content-3'}`}>{STATUS_LABEL[m.status]}</span>
                            </td>
                            {puedeEditar && (
                              <td className="py-2 whitespace-nowrap text-xs">
                                <button type="button" onClick={() => abrirVenta(m)} className="inline-flex items-center gap-1 text-content-2 hover:underline"><RefreshCw className="h-3 w-3" /> {m.status === 'active' ? 'Renovar' : 'Vender otra'}</button>
                                {m.status === 'active' && (
                                  <button type="button" onClick={() => cancelar(m)} disabled={busyId === m.id} className="ml-3 inline-flex items-center gap-1 text-content-3 hover:text-red-600 hover:underline disabled:opacity-50"><X className="h-3 w-3" /> Cancelar</button>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {planModal.open && (
        <MembershipPlanForm plan={planModal.plan} rewards={rewards} loyaltyMode={loyaltyMode}
          onClose={() => setPlanModal({ open: false, plan: null })}
          onSaved={(p) => { setPlanModal({ open: false, plan: null }); toast.success(planModal.plan ? 'Plan guardado' : `Plan «${p.name}» creado`); cargarPlanes(); }} />
      )}
      {sell.open && (
        <SellMembershipModal contact={sell.contact} current={sell.current} plans={plans}
          onClose={() => setSell({ open: false, contact: null, current: null })}
          onSaved={(f, c) => { setSell({ open: false, contact: null, current: null }); toast.success(f.action === 'renewal' ? `Membresía de ${c.name} renovada` : `Membresía vendida a ${c.name}`); cargarMiembros(filter); cargarPlanes(); }} />
      )}
    </Layout>
  );
};

export default MembershipsPage;
